/**
 * Created by fers on 26.09.15.
 */

import { v4 as uuidv4 } from 'uuid';
import {declareEntity} from '../fw/MetaDataHelper.ts';
import { DEFAULT_CURRENCY } from '../locales';
import * as Permission from "../util/Permission";
import {BillingStatus, VersionStrategy, VERSION_STRATEGY_DEVELOPMENT, VERSION_STRATEGY_FROM_ORGANISATION} from './OrganisationType';

export const CACHING_STATUS_NONE = "none";
export const CACHING_STATUS_IN_PROGRESS = "in-progress";
export const CACHING_STATUS_FAILED = "caching-failed";
export const CACHING_STATUS_CACHED = "cached";

export const DEFAULT_EVALUATION_DURATION = 14;

export default declareEntity({
    entity: "flows",
    graphQL: "Flow",
    name: "My Flows",
    dynamoPrefix: 'org',
    storageType: 'central',
    parentRelation: "flows",
    defaultTitle: (data) => {
        if (data['name']) {
            return data['name'];
        } else if (data['flowDef'] && data['flowDef']['name']) {
            return data['flowDef']['name'];
        }
    },
    writePermission: [
        Permission.PERM_EDIT_CHANNEL,
    ],
    fields: (types) => { return {
        organisation: {
            name: "Organisation ID",
            type: "relation",
            to: () => types['organisations'],
            defaultValue: (organisation) => {
                return organisation['id'];
            },
            required: true,
            parent: true,
            hiddenCreate: true,
        },
        instance: {
            name: "Instance",
            type: "relation",
            to: () => types['instances'],
            userEditable: false,
            hiddenCreate: true,
            defaultValue: (parent) => {
                if (parent) {
                    return parent['instance_id'];
                }
            },
        },
        id: {
            name: "My Flow ID",
            type: "id",
            defaultValue: () => {
                return uuidv4();
            },
            primaryKey: true,
            required: true,
            userEditable: false,
            compositeKey: true,
        },
        deduplicationId: {
            name: "Deduplication ID",
            description: "Deduplication ID that is used when writing data after a flow move",
            type: "string",
            userEditable: false,
        },
        externalId: {
            name: "External ID",
            type: "string",
            defaultValue: "",
            filterable: true,
        },
        name: {
            name: "Name",
            type: "string",
            defaultValue: "",
        },
        groupName: {
            name: "Group Name",
            type: "string",
            defaultValue: "",
        },
        description: {
            name: "Description",
            type: "text",
            defaultValue: ""
        },
        creationReason: {
            name: "Creation Reason",
            type: "string",
            defaultValue: "",
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        implementationDetails: {
            name: "Implementation Details",
            type: "text",
            defaultValue: "",
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        secret: {
            name: "Secret",
            type: "string",
            userEditable: false,
            hideInApi: true,
            defaultValue: () => {
                return uuidv4();
            },
        },
        emailStartBilling: {
            name: "E-mail to start billing sent",
            type: "datetime",
            userEditable: false,
        },
        flowDef_id: {
            name: "Flow Definition ID",
            to: () => types['flowDefs'],
            type: "id",
            required: true,
            filterable: true,
        },
        flowDef: {
            name: "Flow Definition",
            to: () => types['flowDefs'],
            type: "relation",
            userEditable: false,
            required: true,
            noStore: true,
            controllerFn: "getFlowDef",
        },
        enabled: {
            name: "Enabled",
            type: "boolean",
            defaultValue: true,
            filterable: true,
        },
        paused: {
            name: "Paused",
            type: "boolean",
            defaultValue: false,
            filterable: true,
        },
        cronSpread: {
            name: "Cron spread",
            type: "number",
            userEditable: false,
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        debug: {
            name: "Debug",
            type: "boolean",
            userEditable: false,
            import: false,
            export: false,
            isPublic: true,
            controllerFn: "isDebug",
        },
        debugUntil: {
            name: "Debug Until",
            type: "datetime",
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        deleted: {
            name: "Deleted",
            type: "boolean",
            defaultValue: false,
			filterable: true,
        },
        deleteReason: {
            name: "Delete Reason",
            type: "string",
        },
        invalid: {
            name: "Invalid",
            type: "boolean",
            defaultValue: false,
			filterable: true,
        },
        hasInvalidChannel: {
            name: "Has Invalid Channel",
            type: "boolean",
            userEditable: false,
            import: false,
            isPublic: true,
            controllerFn: "hasInvalidChannel",
        },
        status: {
            name: "Status",
            type: "object",
            userEditable: false,
            import: false,
            isPublic: true,
            controllerFn: "getFlowStatus",
        },
        receivedAnyMails: {
            name: "Received Any Mails",
            type: "boolean",
            userEditable: false,
        },
        firstSuccessfulTransferAt: {
            name: "First successful transfer At",
            type: "datetime",
            userEditable: false,
        },
        flowTitle: {
            name: "Flow Title",
            type: "string",
            userEditable: false,
            import: false,
            isPublic: true,
            controllerFn: "getFlowTitle",
        },
        activated: {
            name: "Activated",
            type: "boolean",
            defaultValue: false,
            filterable: true,
        },
        lastInvocationAt: {
            name: "Last Invocation",
            type: "datetime",
            userEditable: false,
        },
        lastInvocationErrorAt: {
            name: "Last Invocation Error",
            type: "datetime",
            userEditable: false,
        },
        lastInvocationError: {
            name: "Last Error",
            type: "text",
            userEditable: false,
        },
        versionStrategy: {
            name: "Version Strategy",
            type: "enum",
            values: VersionStrategy,
            defaultValue: VERSION_STRATEGY_FROM_ORGANISATION,
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        flowDefVersion: {
            name: "Flow Definition Version",
            type: "relation",
            to: () => types['flowDefVersions'],
            import: false,
            export: false,
            filterable: true,
            permission: Permission.PERM_INSTANCE_ADMIN,
            visibility: (data) => {
                return data['versionStrategy'] !== VERSION_STRATEGY_DEVELOPMENT;
            },
        },
        rateLimit: {
            name: "Rate Limit",
            description: "Rate limit in format: user=1000/1m, global=1000/1h. Entries without a prefix are treated as user limits, for example 1000/1d = user=1000/1d. Multiple limits can be combined; each request must satisfy all of them.",
            type: "string",
            permission: Permission.PERM_INSTANCE_ADMIN,
            defaultValue: "",
        },
        billingStatus: {
            name: "Billing status",
            type: "enum",
            userEditable: false,
            filterable: true,
            defaultValue: "",
            values: BillingStatus,
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        billedBy: {
            name: "Bill together with",
            type: "relation",
            to: () => types['organisations'],
            userEditable: true,
            description: "Bill this organisation as part of another organisation."
        },
        billInstallation: {
            name: "Bill installation",
            type: "boolean",
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        autobill: {
            name: "Automatic billing",
            type: "boolean",
            filterable: true,
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        billedUsing: {
            name: "Billed using",
            type: "enum",
            permission: Permission.PERM_INSTANCE_BILLING,
            values: {
                "default": {
                    name: "Default",
                },
                "shopify": {
                    name: "Shopify",
                }
            },
            userEditable: false,
            defaultValue: "default",
        },
        billingStartedBy: {
            name: "Billing Started By",
            type: "relation",
            to: () => types['users'],
            userEditable: false,
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        billingStartedAt: {
            name: "Billed Started At",
            type: "date",
            userEditable: false,
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        billingSince: {
            name: "Billing since",
            type: "date",
            userEditable: false,
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        billingValidTo: {
            name: "Billing status",
            type: "datetime",
            filterable: true,
            userEditable: false,
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        billingNote: {
            name: "Billing note",
            type: "text",
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        overridePricePerInvocation: {
            name: "Price per invocation",
            type: "number",
            permission: Permission.PERM_INSTANCE_BILLING,
            addonAfter: DEFAULT_CURRENCY,
        },
        overrideTablePerInvocation: {
            name: "Table per invocation",
            type: "string",
            permission: Permission.PERM_INSTANCE_BILLING,
            addonAfter: DEFAULT_CURRENCY,
        },
        overridePricePerInstance: {
            name: "Price per instance",
            type: "number",
            permission: Permission.PERM_INSTANCE_BILLING,
            addonAfter: DEFAULT_CURRENCY,
        },
        overridePricePerInstanceNext: {
            name: "Price per instance (more)",
            type: "number",
            permission: Permission.PERM_INSTANCE_BILLING,
            addonAfter: DEFAULT_CURRENCY,
        },
        overrideMinPrice: {
            name: "Minimum Price",
            type: "number",
            permission: Permission.PERM_INSTANCE_BILLING,
            addonAfter: DEFAULT_CURRENCY,
        },
        hasCustomCode: {
            name: "Has Custom Code",
            type: "boolean",
            filterable: true,
            userEditable: false,
            import: false,
        },
        adminExtraEmail: {
            name: "Admin extra emails",
            type: "text",
            trim: true,
            defaultValue: "",
        },
        author: {
            name: "Created By",
            type: "relation",
            to: () => types['users'],
            userEditable: false,
        },
        appliedDescription: {
            name: "Applied Description",
            type: "text",
            controllerFn: 'getAppliedDescription',
        },
        disabledAt: {
            name: "Disabled At",
            type: "datetime",
            userEditable: false,
        },
        resumedAt: {
            name: "Resumed At",
            type: "datetime",
            userEditable: false,
        },
        deletedAt: {
            name: "Deleted At",
            type: "datetime",
            userEditable: false,
        },
        updatedAt: {
            name: "Updated At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        billingInfo: {
            name: "Billing Info",
            type: "object",
            //userEditable: false,
            //import: false,
            //export: false,
            controllerFn: "getBillingInfo",
        },
        createdAt: {
            name: "Created At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        lastGeneratedBilling: {
            name: "Last generated billing",
            type: "date",
            userEditable: false,
            permission: Permission.PERM_INSTANCE_BILLING,
        },
        selectedChannels: {
            name: "Selected Applications",
            type: "object",
            defaultValue: {},
        },
        options: {
            name: "Options",
            type: "object",
            defaultValue: {},
        },
        coordinationListeners: {
            name: "Listeners",
            type: "object",
            defaultValue: {},
        },
        encryptKey: {
            name: "Encrypt Key",
            type: "string",
            userEditable: false,
            import: false,
            export: false,
            hideInApi: true,
            defaultValue: () => {
                return uuidv4().substring(0, 16).replace("-", "");
            },
            visibility: () => { return false; },
        },
        state: {
            name: "Internal State",
            type: "object",
            defaultValue: {},
            userEditable: false,
        },
        configOptions: {
            name: "ConfigOptions",
            type: "object",
            //userEditable: false,
            //import: false,
            //export: false,
            controllerFn: "getConfigOptions",
        },
        channelErrorTypes: {
            name: "Channel Error Types",
            type: "object",
            defaultValue: [],
            args: {
                author_id: {
                    type: "string"
                },
                displayNameLike: {
                    type: "string"
                },
                resolvableBy: {
                    type: "string"
                },
                type: {
                    type: "string"
                },
                hasError: {
                    type: "boolean"
                },
                lastErrorTransferAtAfter: {
                    type: "datetime"
                },
                lastErrorTransferAtBefore: {
                    type: "datetime"
                },
            },
            controllerFn: 'getChannelErrorsGroupBy',
        },
		scenario: {
			name: "Scenario",
			type: "relation",
			to: () => types['scenarios'],
			defaultValue: (scenario) => {
				return scenario['scenario_id'];
			},
			parent: true,
			hiddenCreate: true,
		},
    }; },
    relations: (types) => { return {
        channels: {
            type: 'oneToMany',
            to: types['channels'],
            controllerFn: 'getChannels',
        },
        invocations: {
            type: 'oneToMany',
            to: types['flowInvocations'],
            args: {
                flowIncomingEmail_id: {
                    type: 'ID',
                    asFilter: true,
                },
            },
            IndexName: 'flow_id-index',
            foreignKeys: [
                {
                    from: "id",
                    to: "flow_id"
                }
            ]
        },
        coordinationFlowsToNotify: {
            type: 'oneToMany',
            to: types['flows'],
            controllerFn: 'getCoordinationFlowsToNotify',
        },
        events: {
            type: 'manyToOne',
            to: types['organisationEvents'],
            foreignKeys: [
                {
                    from: "organisation_id",
                    to: "organisation_id"
                },
                {
                    from: "id",
                    to: "flow_id"
                },
            ],
        },
        fetches: {
            type: 'oneToMany',
            to: types['fetches'],
            foreignKeys: [
                {
                    from: "organisation_id",
                    to: "organisation_id"
                },
                {
                    from: "id",
                    to: "flow_id"
                }
            ]
        },
        billingItems: {
            type: 'oneToMany',
            to: types['billingItems'],
            foreignKeys: [
                {
                    from: "organisation_id",
                    to: "organisation_id"
                },
                {
                    from: "id",
                    to: "flow_id"
                }
            ]
        },
        runningJobs: {
            type: 'oneToMany',
            to: types['runningJobs'],
            foreignKeys: [
                {
                    from: "organisation_id",
                    asFilterOnly: true,
                    to: "organisation_id"
                },
                {
                    from: "id",
                    asFilterOnly: true,
                    to: "flow_id"
                }
            ]
        },
        customDatas: {
            type: 'oneToMany',
            to: types['customDatas'],
            IndexName: 'organisation_channel_flow-key-index',
            foreignKeys: [
                {
                    from: "organisation_id|id",
                    to: "organisation_channel_flow",
                }

                // {
                //     from: "organisation_id",
                //     to: "organisation_id",
                // },
                // {
                //     from: "id",
                //     to: "flow_id",
                // }
            ]
        },
        hooks: {
            type: 'oneToMany',
            to: types['hooks'],
            foreignKeys: [
                {
                    from: "organisation_id",
                    to: "organisation_id"
                },
                {
                    from: "id",
                    asFilterOnly: true,
                    to: "flow_id"
                }
            ]
        },
        manualButtons: {
            type: 'oneToMany',
            to: types['hooks'],
            controllerFn: 'getManualButtons',
        },
        manualForms: {
            type: 'oneToMany',
            to: types['hooks'],
            controllerFn: 'getManualForms',
        },
        adminUsers: {
            type: 'manyToOne',
            to: types['users'],
            controllerFn: 'getAdminUsers',
        },
        logger: {
            type: 'oneToMany',
            to: types['loggers'],
            foreignKeys: [
                {
                    from: "id",
                    to: "flow_id"
                },
                {
                    from: "instance_id",
                    to: "instance_id"
                },
            ]
        },
        channelErrors: {
            type: 'oneToMany',
            to: types['channelErrors'],
            foreignKeys: [
                {
                    from: "id",
                    to: "flow_id"
                },
                {
                    from: "instance_id",
                    to: "instance_id"
                },
            ]
        },
        flowIncomingEmails: {
            type: 'oneToMany',
            to: types['flowIncomingEmails'],
            foreignKeys: [
                {
                    from: "id",
                    to: "flow_id"
                },
                {
                    from: "instance_id",
                    to: "instance_id"
                },
            ]
        },

    }; }
});
